import { useEffect, useState } from "react";
import { BookOpen, Lock, Loader2, RotateCcw, Pencil, Plus, Upload, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import LoadError from "@/components/dashboard/LoadError";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import {
  listQaContextProjects, getQaContext, saveQaContextSection, revertQaContextSection,
  importQaContext,
} from "@/api/admin";
import { toastError, toastSuccess } from "@/lib/toast";

const DEMO = { projects: [{ id: "p1", name: "Demo project", has_repo: true, edited_sections: 0 }] };

// What the parser actually read out of the file, which is the only honest measure of
// an edit: a section rewritten under a heading the parser does not recognise still
// saves, still reads correctly to a human, and reaches no generated case at all.
const COUNTS = [
  ["vocabulary", "Vocabulary"], ["enums", "Enums"], ["journeys", "Journeys"],
  ["business_rules", "Business rules"], ["do_not_test", "Do not test"],
  ["never_submit", "Never submit"], ["golden_fixtures", "Fixtures"],
];

export default function QaContext() {
  const { data, loading, demo, error, reload } = useApiData(listQaContextProjects, DEMO);
  const projects = (data && data.projects) || [];
  const [projectId, setProjectId] = useState("");
  const [doc, setDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // the section open in the editor
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  // Shown in the modal footer, not in page flow: the modal is a scroll-locked overlay
  // and a banner behind its scrim cannot be read.
  const [saveError, setSaveError] = useState("");
  // The entries this save would REMOVE from a safety list. Held rather than acted on:
  // adding to a guard applies at once, giving one up is shown first, in full, and
  // takes a second press. A wrong addition costs a manual case; a wrong removal costs
  // a deletion in a customer's live system.
  const [weakening, setWeakening] = useState(null);
  // The file waiting to be imported. Held between the read and the POST so the
  // weakening confirmation can re-send exactly the same text on the second press.
  const [pendingImport, setPendingImport] = useState(null);
  const [importWarnings, setImportWarnings] = useState([]);
  // The credential-bearing lines this file carries. Shown as NUMBERS with the option
  // to import without them: refusing two thousand lines for two is the wrong trade,
  // and quoting the line back would publish the credential into a toast.
  const [secrets, setSecrets] = useState(null);

  useEffect(() => {
    // Everything below is ABOUT one project, so none of it may outlive the choice of
    // project. Left in place, the previous project's import result reads as this
    // one's: a warning that three credential lines were dropped, shown against a
    // project nobody had imported anything into.
    setImportWarnings([]);
    setSecrets(null);
    setWeakening(null);
    setPendingImport(null);
    setEditing(null);
    setSaveError("");
    if (!projectId) { setDoc(null); return; }
    let live = true;
    setDocLoading(true);
    getQaContext(projectId)
      .then((d) => { if (live) setDoc(d); })
      .catch((e) => { if (live) { setDoc(null); toastError(e); } })
      .finally(() => { if (live) setDocLoading(false); });
    return () => { live = false; };
  }, [projectId]);

  async function refresh() {
    if (!projectId) return;
    try { setDoc(await getQaContext(projectId)); } catch (e) { toastError(e); }
  }

  function open(section) {
    setSaveError("");
    setEditing(section);
    setBody(section.body || "");
  }

  // A project whose repo has no file has NO sections, so without this the screen
  // shows nothing to press — and that is exactly the project that most needs saying
  // something about. The starter body already parses; see qa_context_edit.TEMPLATES.
  function add(key) {
    const template = (doc?.available || []).find((t) => t.key === key);
    if (!template) return;
    setSaveError("");
    setEditing({ key: template.key, title: template.title, help: template.help });
    setBody(template.body);
  }

  async function save(confirmWeakening = false) {
    setBusy(true);
    setSaveError("");
    try {
      await saveQaContextSection(projectId, editing.key, {
        body, title: editing.title, confirm_weakening: confirmWeakening,
      });
      setWeakening(null);
      setEditing(null);
      await refresh();
      toastSuccess("Saved. It applies to the next generation run.");
    } catch (e) {
      // A removal from a safety list comes back as 409 with the exact entries being
      // given up, so the confirmation can name them instead of asking "are you sure?"
      const detail = e?.detail || e?.data?.detail;
      if (detail?.reason === "weakening") {
        setWeakening(detail);
      } else {
        // The other refusal — a credential in the body — is the whole point of this
        // screen's server side, so it is shown verbatim rather than as "save failed".
        setSaveError(e?.message || String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function runImport(text, confirmWeakening = false, redact = false) {
    setBusy(true);
    try {
      const out = await importQaContext(projectId, {
        text, confirm_weakening: confirmWeakening, redact_secrets: redact,
        replace_all: false,
      });
      setWeakening(null);
      setSecrets(null);
      setPendingImport(null);
      // Warnings are not failures, and they are not decoration either: a yaml block
      // above the first heading is silently absent from what generation will read.
      setImportWarnings([
        ...(out.warnings || []),
        ...(out.redacted?.length
          ? [`${out.redacted.length} line(s) were left out because they looked like `
             + `credentials: ${out.redacted.map((h) => h.line).join(", ")}.`]
          : []),
      ]);
      await refresh();
      toastSuccess(`Imported ${out.sections.length} section(s).`);
    } catch (e) {
      const detail = e?.detail || e?.data?.detail;
      if (detail?.reason === "weakening") {
        setPendingImport(text);
        setWeakening(detail);
      } else if (detail?.reason === "secrets") {
        setPendingImport(text);
        setSecrets(detail);
      } else {
        toastError(e);
      }
    } finally {
      setBusy(false);
    }
  }

  function pickFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";              // so choosing the same file twice still fires
    if (!file) return;
    setImportWarnings([]);
    const reader = new FileReader();
    reader.onload = () => runImport(String(reader.result || ""));
    reader.onerror = () => toastError(new Error("That file could not be read."));
    reader.readAsText(file);
  }

  async function revert(section) {
    try {
      await revertQaContextSection(projectId, section.key);
      await refresh();
      toastSuccess("Back to the repository's version.");
    } catch (e) { toastError(e); }
  }

  if (error) return <LoadError error={error} onRetry={reload} />;

  const summary = doc?.summary || {};

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BookOpen}
        title="QA Context"
        subtitle="What each project's docs/qa-context.md says about its application — edited here, section by section."
      />

      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Project
        </label>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={loading || demo}
          className="mt-2 w-full max-w-xl rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
        >
          <option value="">Choose a project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.has_repo ? "" : " — no repository"}
              {p.edited_sections ? ` — ${p.edited_sections} edited` : ""}
            </option>
          ))}
        </select>

        {projectId && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500">
              <Upload className="h-4 w-4" />
              Import a qa-context.md
              <input type="file" accept=".md,.markdown,text/markdown,text/plain"
                     className="hidden" onChange={pickFile} disabled={busy} />
            </label>
            <span className="text-xs text-slate-500">
              Reads the file straight into this project, section by section — no
              repository access needed. Sections it does not contain are left alone.
            </span>
          </div>
        )}
      </div>

      {importWarnings.length > 0 && (
        <div className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <ul className="space-y-1">
            {importWarnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {docLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading the repository…
        </div>
      )}

      {doc && !docLoading && (
        <>
          {!doc.has_repo_file && (
            <div className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div>
                {doc.why_no_file || "No docs/qa-context.md was found for this project."}
                <div className="mt-1">
                  Anything added below still reaches generation; it is simply not backed
                  by a file in the repository.
                </div>
              </div>
            </div>
          )}

          {doc.orphans?.length > 0 && (
            <div className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div>
                Edited here, but no longer in the repository&apos;s file (renamed or removed
                upstream): <b>{doc.orphans.join(", ")}</b>. The text is still applied —
                reconcile it with the repo rather than leaving the two disagreeing.
              </div>
            </div>
          )}

          {/* The parser's own reading of the effective document. */}
          <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            {summary.parsed === false ? (
              <span className="text-sm text-rose-600">
                This file could not be parsed: {summary.error}
              </span>
            ) : (
              COUNTS.map(([key, label]) => (
                <Badge key={key} variant={summary[key] ? "default" : "outline"}>
                  {label}: {summary[key] ?? 0}
                </Badge>
              ))
            )}
          </div>

          {doc.available?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">
                {doc.sections.length ? "Add a section" : "Start describing this project"}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Each one opens with an example that already works — edit it to match
                this product. Nothing here is written to the repository.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {doc.available.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => add(t.key)}
                    className="rounded-lg border border-slate-200 p-3 text-left hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500"
                  >
                    <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                      <Plus className="h-4 w-4" /> {t.title}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{t.help}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {doc.sections.map((s) => (
              <div
                key={s.key}
                className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-medium text-slate-900 dark:text-slate-100">
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </h3>
                      {s.safety && (
                        <Badge variant="outline" className="gap-1 border-rose-300 text-rose-700 dark:border-rose-800 dark:text-rose-300">
                          <Lock className="h-3 w-3" /> Arms a guard
                        </Badge>
                      )}
                      {s.edited && <Badge>Edited here</Badge>}
                    </div>
                    {s.safety && (
                      <p className="mt-1 text-xs text-slate-500">{s.safety_reason}</p>
                    )}
                    {s.edited && (
                      <p className="mt-1 text-xs text-slate-500">
                        by {s.edited_by} · {s.edited_at ? new Date(s.edited_at).toLocaleString() : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {s.edited && (
                      <Button variant="outline" size="sm" onClick={() => revert(s)}>
                        <RotateCcw className="mr-1 h-4 w-4" /> Revert
                      </Button>
                    )}
                    <Button size="sm" onClick={() => open(s)}>
                      <Pencil className="mr-1 h-4 w-4" /> Edit
                    </Button>
                  </div>
                </div>
                <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
                  {s.body || "— empty —"}
                </pre>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Edit — ${editing.title}` : ""}
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-xs text-rose-600">{saveError}</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save(false)} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
              </Button>
            </div>
          </div>
        }
      >
        {editing?.help && (
          <p className="mb-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-950 dark:text-slate-300">
            {editing.help}
          </p>
        )}
        <p className="mb-2 text-xs text-slate-500">
          Markdown, as it appears in the file. This is read into every generation prompt
          for the project — never put a password, token or key in it. Saved edits apply
          to the next generation run; the repository is not written to.
        </p>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={18}
          maxLength={doc?.max_body_chars || 20000}
          className="w-full rounded-lg border border-slate-300 bg-white p-3 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
        />
        <div className="mt-1 text-right text-xs text-slate-400">
          {body.length} / {doc?.max_body_chars || 20000}
        </div>
      </Modal>

      <Modal
        open={!!secrets}
        onClose={() => { setSecrets(null); setPendingImport(null); }}
        title="This file carries a credential"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline"
                    onClick={() => { setSecrets(null); setPendingImport(null); }}>
              Cancel
            </Button>
            <Button onClick={() => runImport(pendingImport, false, true)} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import without those lines
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{secrets?.message}</p>
        <ul className="mt-3 space-y-1 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
          {(secrets?.hits || []).map((h) => (
            <li key={h.line}>Line {h.line} — {h.kind}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Those lines are dropped, not stored anywhere. Everything else in the file is
          imported. The app signs in with the project&apos;s own saved credentials, so a
          login written in this document was never used for anything.
        </p>
      </Modal>

      {/* Removing from a guard names exactly what is being given up. "Are you sure?"
          asks nothing; this asks the only question worth asking. */}
      <Modal
        open={!!weakening}
        onClose={() => setWeakening(null)}
        title="This gives up a safety rule"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setWeakening(null); setPendingImport(null); }}
            >
              Keep it
            </Button>
            <Button
              onClick={() => (pendingImport ? runImport(pendingImport, true) : save(true))}
              disabled={busy}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove anyway
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{weakening?.message}</p>
        <ul className="mt-3 space-y-1 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
          {(weakening?.removed || []).map((r) => <li key={r}>— {r}</li>)}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Recorded in the audit log against your account. The platform&apos;s own limits stay
          either way: the crawler still refuses labels like Delete, Pay and Suspend, and
          the runner still refuses an irreversible action.
        </p>
      </Modal>
    </div>
  );
}
